import { Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { useMachine } from '@xstate/react';
import { MonitorUp, RefreshCw, Settings, Terminal } from 'lucide-react';
import { useRegisterSW } from 'virtual:pwa-register/react';

import { api, ApiError, type AuthenticatedSession, type LoginChallenge } from './app/api';
import { subscribeAppCenterChanged } from './app/app-center-events';
import { queueIsolatedBrowserLaunch } from './app/browser-launch';
import { appCatalog } from './app/catalog';
import { sessionMachine } from './app/session-machine';
import { DEFAULT_WEBOS_SETTINGS, type AppCenterApp, type AppId, type Bounds, type ContextMenuState, type SessionInfo, type WebosNotification, type WebosSettings, type WindowState, type WorkspaceState } from './app/types';
import { installVisualViewportSync } from './app/visual-viewport';
import {
  activateSnapGroup, closeWindow, createDesktop, createSnapGroup, cycleDesktop, cycleWindow, defaultWorkspace, focusWindow, launchWindow, maximizeWindow,
  minimizeWindow, moveWindow, moveWindowToDesktop, normalizeWorkspace, removeDesktop, renameDesktop, setDesktopWindowsMinimized,
  snapWindow, switchDesktop
} from './app/window-state';
import { ContextMenu } from './components/ContextMenu';
import { LoginScreen } from './components/LoginScreen';
import { ShellChrome } from './components/ShellChrome';
import { WindowFrame } from './components/WindowFrame';

interface InstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export default function App() {
  const [machine, send] = useMachine(sessionMachine);
  const [session, setSession] = useState<SessionInfo | null>(null);
  const [loginError, setLoginError] = useState('');
  const [workspace, setWorkspace] = useState<WorkspaceState>(defaultWorkspace);
  const workspaceRef = useRef(workspace);
  workspaceRef.current = workspace;
  const [settings, setSettings] = useState<WebosSettings>(DEFAULT_WEBOS_SETTINGS);
  const [availableApps, setAvailableApps] = useState<Set<AppId>>(new Set());
  const [managedApps, setManagedApps] = useState<AppCenterApp[]>([]);
  const settingsRef = useRef(settings);
  settingsRef.current = settings;
  const workspaceRevision = useRef(0);
  const settingsRevision = useRef(0);
  const hydrated = useRef(false);
  const workspaceSaveInFlight = useRef(false);
  const workspaceSavePending = useRef(false);
  const lastAckedWorkspace = useRef('');
  const settingsSaveQueue = useRef(Promise.resolve());
  const clientId = useRef(crypto.randomUUID());
  const [syncState, setSyncState] = useState<'saved' | 'saving' | 'offline' | 'conflict'>('saved');
  const [online, setOnline] = useState(navigator.onLine);
  const [startOpen, setStartOpen] = useState(false);
  const [quickOpen, setQuickOpen] = useState(false);
  const [noticesOpen, setNoticesOpen] = useState(false);
  const [taskViewOpen, setTaskViewOpen] = useState(false);
  const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(null);
  const [notices, setNotices] = useState<WebosNotification[]>([]);
  const [installPrompt, setInstallPrompt] = useState<InstallPromptEvent | null>(null);

  useEffect(() => installVisualViewportSync(), []);
  const [snapAssist, setSnapAssist] = useState<{ windowId: string; bounds: Bounds } | null>(null);
  const showDesktopWindows = useRef<Set<string>>(new Set());
  const serviceWorkerRegistration = useRef<ServiceWorkerRegistration | null>(null);

  const { needRefresh: [needRefresh, setNeedRefresh], updateServiceWorker } = useRegisterSW({
    onRegisteredSW(_url, registration) { serviceWorkerRegistration.current = registration || null; },
    onRegisterError(error) { console.error('Service worker registration failed', error); }
  });

  useEffect(() => {
    const checkForUpdate = () => {
      if (!document.hidden) void serviceWorkerRegistration.current?.update();
    };
    const timer = window.setInterval(checkForUpdate, 5 * 60 * 1_000);
    window.addEventListener('focus', checkForUpdate);
    window.addEventListener('online', checkForUpdate);
    document.addEventListener('visibilitychange', checkForUpdate);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('focus', checkForUpdate);
      window.removeEventListener('online', checkForUpdate);
      document.removeEventListener('visibilitychange', checkForUpdate);
    };
  }, []);

  const reportStatus = useCallback((message: string, tone: WebosNotification['tone'] = 'info') => {
    const optimistic: WebosNotification = {
      id: crypto.randomUUID(), title: 'NextOS', message, tone,
      actionApp: null, actionLabel: null, readAt: null, createdAt: new Date().toISOString()
    };
    setNotices(items => [optimistic, ...items].slice(0, 100));
    void api.createNotification({ message, tone }).then(saved => {
      setNotices(items => [saved, ...items.filter(item => item.id !== optimistic.id && item.id !== saved.id)].slice(0, 100));
    }).catch(() => {
      setNotices(items => items.map(item => item.id === optimistic.id ? { ...item, readAt: item.createdAt } : item));
    });
  }, []);

  const loadWorkspace = useCallback(async () => {
    const [workspacePayload, settingsPayload, appsPayload, noticesPayload, appCenterPayload] = await Promise.all([
      api.workspace(), api.settings(), api.apps(), api.notifications().catch(() => ({ notifications: [] as WebosNotification[] })),
      api.appCenterCatalog().catch(() => null)
    ]);
    const nextAvailableApps = new Set(appsPayload.apps
      .filter(app => app.available !== false && app.id in appCatalog)
      .map(app => app.id));
    nextAvailableApps.add('home');
    let normalized = normalizeWorkspace(workspacePayload.state, nextAvailableApps);
    if (!normalized.windows.length) normalized = launchWindow(normalized, appCatalog.home);
    workspaceRevision.current = workspacePayload.revision;
    settingsRevision.current = settingsPayload.revision;
    const nextSettings = { ...DEFAULT_WEBOS_SETTINGS, ...settingsPayload.settings, schemaVersion: 5, shellLayout: 'windows' } as WebosSettings;
    setWorkspace(normalized);
    setSettings(nextSettings);
    setAvailableApps(nextAvailableApps);
    setManagedApps(appCenterPayload?.apps.filter(app => app.installed) || []);
    setNotices(noticesPayload.notifications);
    lastAckedWorkspace.current = JSON.stringify(normalized);
    hydrated.current = true;
    send({ type: 'WORKSPACE_READY' });
  }, [send]);

  useEffect(() => subscribeAppCenterChanged(() => {
    void api.appCenterCatalog().then(payload => setManagedApps(payload.apps.filter(app => app.installed))).catch(() => {});
  }), []);

  useEffect(() => {
    void api.session().then(nextSession => {
      setSession(nextSession);
      send({ type: 'SESSION_FOUND' });
      return loadWorkspace();
    }).catch(error => {
      if (error instanceof ApiError && error.status === 401) send({ type: 'SESSION_MISSING' });
      else send({ type: 'FAILED' });
    });
  }, [loadWorkspace, send]);

  const login = async (username: string, password: string): Promise<LoginChallenge> => {
    setLoginError('');
    send({ type: 'LOGIN_STARTED' });
    try {
      const challenge = await api.login(username, password);
      send({ type: 'LOGIN_CHALLENGE' });
      return challenge;
    } catch {
      setLoginError('Benutzer oder Passwort ist nicht korrekt.');
      send({ type: 'LOGIN_FAILED' });
      throw new Error('INVALID_LOGIN');
    }
  };

  const authenticated = async (nextSession: AuthenticatedSession) => {
    setSession(nextSession);
    send({ type: 'LOGIN_SUCCESS' });
    await loadWorkspace();
  };

  const logout = async () => {
    await api.logout().catch(() => {});
    api.setSession(null);
    hydrated.current = false;
    setSession(null);
    setWorkspace(defaultWorkspace);
    setManagedApps([]);
    send({ type: 'LOGOUT' });
  };

  const saveWorkspace = useCallback(async () => {
    if (!hydrated.current || !session || workspaceSaveInFlight.current || !navigator.onLine) {
      workspaceSavePending.current = true;
      if (!navigator.onLine) setSyncState('offline');
      return;
    }
    const serialized = JSON.stringify(workspaceRef.current);
    if (serialized === lastAckedWorkspace.current) return;
    workspaceSaveInFlight.current = true;
    workspaceSavePending.current = false;
    setSyncState('saving');
    try {
      const result = await api.saveWorkspace(workspaceRef.current, workspaceRevision.current, clientId.current);
      workspaceRevision.current = result.revision;
      lastAckedWorkspace.current = serialized;
      setSyncState('saved');
    } catch (error) {
      if (error instanceof ApiError && error.status === 409) {
        setSyncState('conflict');
        reportStatus('Der Arbeitsbereich wurde in einer anderen Sitzung geändert. Der lokale Stand wurde nicht überschrieben.', 'danger');
      } else if (error instanceof ApiError && error.status === 401) {
        send({ type: 'SESSION_MISSING' });
      } else {
        workspaceSavePending.current = true;
        setSyncState('offline');
      }
    } finally {
      workspaceSaveInFlight.current = false;
      if (workspaceSavePending.current && navigator.onLine) window.setTimeout(() => void saveWorkspace(), 100);
    }
  }, [reportStatus, send, session]);

  useEffect(() => {
    if (!hydrated.current || JSON.stringify(workspace) === lastAckedWorkspace.current || syncState === 'conflict') return;
    const timer = window.setTimeout(() => void saveWorkspace(), 300);
    return () => window.clearTimeout(timer);
  }, [saveWorkspace, syncState, workspace]);

  const updateSettings = useCallback((patch: Partial<WebosSettings>): Promise<void> => {
    const next = { ...settingsRef.current, ...patch, schemaVersion: 5, shellLayout: 'windows' } as WebosSettings;
    settingsRef.current = next;
    setSettings(next);
    settingsSaveQueue.current = settingsSaveQueue.current.then(async () => {
      const result = await api.saveSettings(settingsRef.current, settingsRevision.current);
      settingsRevision.current = result.revision;
    }).catch(async error => {
      if (error instanceof ApiError && error.status === 409) {
        const fresh = await api.settings();
        settingsRevision.current = fresh.revision;
        const merged = { ...DEFAULT_WEBOS_SETTINGS, ...fresh.settings, schemaVersion: 5, shellLayout: 'windows' } as WebosSettings;
        settingsRef.current = merged;
        setSettings(merged);
        reportStatus('Einstellungen wurden auf den aktuellen Serverstand zurückgesetzt.', 'warning');
      } else reportStatus('Einstellungen konnten nicht gespeichert werden.', 'danger');
    });
    return settingsSaveQueue.current;
  }, [reportStatus]);

  const closePanels = useCallback(() => { setStartOpen(false); setQuickOpen(false); setNoticesOpen(false); setTaskViewOpen(false); }, []);
  const launchApp = useCallback((appId: AppId) => {
    closePanels();
    setSnapAssist(null);
    if (!availableApps.has(appId)) {
      reportStatus(`${appCatalog[appId].name} ist auf diesem Server nicht konfiguriert.`, 'warning');
      return;
    }
    setWorkspace(state => launchWindow(state, appCatalog[appId]));
  }, [availableApps, closePanels, reportStatus]);

  const launchManagedApp = useCallback(async (appId: string) => {
    try {
      const launch = await api.launchAppCenterApp(appId);
      queueIsolatedBrowserLaunch(launch.appId, launch.url);
      launchApp('browser');
      reportStatus(`${launch.name} wird sicher im isolierten Browser geöffnet.`, 'success');
    } catch (error) {
      if (error instanceof ApiError && error.status === 409) {
        reportStatus('Diese App ist nicht installiert. Installiere sie zuerst im App Center.', 'warning');
      } else {
        reportStatus('Die App konnte nicht sicher gestartet werden.', 'danger');
      }
      throw error;
    }
  }, [launchApp, reportStatus]);

  useEffect(() => {
    if (!hydrated.current) return;
    const requested = new URLSearchParams(location.search).get('launch') as AppId | null;
    if (requested && appCatalog[requested]) {
      launchApp(requested);
      history.replaceState({}, '', location.pathname);
    }
  }, [launchApp, machine.value]);

  useEffect(() => {
    const onOnline = () => { setOnline(true); setSyncState('saving'); send({ type: 'CONNECTION_RESTORED' }); void saveWorkspace(); };
    const onOffline = () => { setOnline(false); setSyncState('offline'); send({ type: 'CONNECTION_LOST' }); };
    const onInstall = (event: Event) => { event.preventDefault(); setInstallPrompt(event as InstallPromptEvent); };
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    window.addEventListener('beforeinstallprompt', onInstall);
    return () => { window.removeEventListener('online', onOnline); window.removeEventListener('offline', onOffline); window.removeEventListener('beforeinstallprompt', onInstall); };
  }, [saveWorkspace, send]);

  useEffect(() => {
    if (!session || machine.value !== 'ready') return;
    const events = new EventSource('/api/v1/events', { withCredentials: true });
    events.addEventListener('open', () => setOnline(true));
    events.addEventListener('message', event => {
      try {
        const message = JSON.parse(String(event.data)) as { type?: string; revision?: number; clientId?: string; notification?: WebosNotification };
        if (message.type === 'workspace.updated' && message.clientId !== clientId.current && Number(message.revision) > workspaceRevision.current) {
          if (JSON.stringify(workspaceRef.current) !== lastAckedWorkspace.current) setSyncState('conflict');
          else void api.workspace().then(payload => {
            workspaceRevision.current = payload.revision;
            const next = normalizeWorkspace(payload.state, new Set(Object.keys(appCatalog) as AppId[]));
            lastAckedWorkspace.current = JSON.stringify(next);
            setWorkspace(next);
          });
        } else if (message.type === 'notification.created' && message.notification) {
          setNotices(items => [message.notification!, ...items.filter(item => item.id !== message.notification!.id)].slice(0, 100));
        }
      } catch {
        setSyncState('conflict');
      }
    });
    events.addEventListener('error', () => setOnline(false));
    return () => events.close();
  }, [machine.value, session]);

  useEffect(() => {
    const keydown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setStartOpen(true);
      } else if (event.altKey && !event.ctrlKey && !event.metaKey && event.key === 'Tab') {
        event.preventDefault();
        setWorkspace(state => cycleWindow(state, event.shiftKey));
      } else if (event.ctrlKey && event.altKey && ['ArrowLeft', 'ArrowRight'].includes(event.key)) {
        event.preventDefault();
        showDesktopWindows.current.clear();
        setWorkspace(state => cycleDesktop(state, event.key === 'ArrowLeft'));
      } else if ((event.metaKey && event.key.toLowerCase() === 'd') || (event.ctrlKey && event.altKey && event.key.toLowerCase() === 'd')) {
        event.preventDefault();
        setWorkspace(state => {
          const visible = state.windows.filter(window => window.desktopId === state.desktopId && !window.minimized);
          if (visible.length) {
            showDesktopWindows.current = new Set(visible.map(window => window.id));
            return setDesktopWindowsMinimized(state, true);
          }
          const restore = showDesktopWindows.current;
          showDesktopWindows.current = new Set();
          return restore.size ? setDesktopWindowsMinimized(state, false, restore) : state;
        });
      } else if ((event.metaKey && event.key === 'Tab') || (event.ctrlKey && event.altKey && event.key === 'Tab')) {
        event.preventDefault();
        setTaskViewOpen(value => !value);
      } else if (event.metaKey && ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) {
        event.preventDefault();
        setWorkspace(state => {
          const id = state.activeWindowId;
          if (!id) return state;
          if (event.key === 'ArrowUp') return maximizeWindow(state, id);
          if (event.key === 'ArrowDown') return minimizeWindow(state, id);
          const width = Math.floor(window.innerWidth / 2);
          return snapWindow(state, id, { x: event.key === 'ArrowLeft' ? 0 : width, y: 0, width: event.key === 'ArrowLeft' ? width : window.innerWidth - width, height: window.innerHeight - 64 });
        });
      } else if (event.ctrlKey && event.altKey && event.key.toLowerCase() === 't') {
        event.preventDefault();
        launchApp('terminal');
      } else if (event.key === 'Escape') closePanels();
      else if (event.shiftKey && event.key === 'F10') {
        event.preventDefault();
        setContextMenu({ x: innerWidth / 2, y: innerHeight / 2, actions: desktopMenuActions() });
      }
    };
    window.addEventListener('keydown', keydown);
    return () => window.removeEventListener('keydown', keydown);
  });

  const desktopMenuActions = () => [
    { id: 'terminal', label: 'Terminal öffnen', icon: Terminal, run: () => launchApp('terminal') },
    { id: 'settings', label: 'Einstellungen', icon: Settings, run: () => launchApp('settings') },
    { id: 'sep', label: '', separator: true, run: () => {} },
    { id: 'refresh', label: 'Arbeitsbereich neu laden', icon: RefreshCw, run: () => location.reload() }
  ];

  const install = async () => {
    if (!installPrompt) return;
    await installPrompt.prompt();
    await installPrompt.userChoice;
    setInstallPrompt(null);
  };

  const snapWithAssist = (windowId: string, bounds: Bounds) => {
    setWorkspace(state => snapWindow(state, windowId, bounds));
    const sideSnap = bounds.y === 0 && bounds.height >= window.innerHeight - 72 && bounds.width >= window.innerWidth * 0.4 && bounds.width <= window.innerWidth * 0.6;
    const hasPartner = workspaceRef.current.windows.some(candidate => candidate.id !== windowId && candidate.desktopId === workspaceRef.current.desktopId && !candidate.minimized);
    setSnapAssist(sideSnap && hasPartner ? { windowId, bounds } : null);
  };

  const completeSnapGroup = (partnerId: string) => {
    if (!snapAssist) return;
    const half = Math.floor(window.innerWidth / 2);
    const opposite: Bounds = snapAssist.bounds.x === 0
      ? { x: half, y: 0, width: window.innerWidth - half, height: window.innerHeight - 64 }
      : { x: 0, y: 0, width: half, height: window.innerHeight - 64 };
    setWorkspace(state => createSnapGroup(snapWindow(state, partnerId, opposite), snapAssist.windowId, partnerId));
    setSnapAssist(null);
  };

  if (machine.value === 'checking' || machine.value === 'loading') return <div className="boot-screen"><img src="/icons/nextos-512.png" alt="" /><span className="boot-spinner" /></div>;
  if (machine.value === 'signedOut' || machine.value === 'authenticating' || machine.value === 'mfa') return <LoginScreen busy={machine.value === 'authenticating'} error={loginError} onLogin={login} onAuthenticated={authenticated} />;
  if (machine.value === 'failed') return <div className="fatal-screen"><MonitorUp /><h1>NextOS konnte nicht gestartet werden.</h1><button onClick={() => location.reload()}>Erneut versuchen</button></div>;
  if (!session) return null;

  return (
    <main
      className={`desktop ${settings.shellLayout}-shell ${settings.reducedTransparency ? 'reduced-transparency' : ''} ${settings.highContrast ? 'high-contrast' : ''} ${settings.reducedMotion ? 'reduced-motion' : ''}`}
      style={{
        '--ui-scale': settings.uiScale,
        width: `${100 / settings.uiScale}%`,
        height: `${100 / settings.uiScale}%`,
        transform: `scale(${settings.uiScale})`
      } as React.CSSProperties}
      onContextMenu={event => {
        if ((event.target as HTMLElement).closest('.window-frame,.taskbar,.start-menu,.quick-panel,.notifications-panel')) return;
        event.preventDefault();
        setContextMenu({ x: event.clientX, y: event.clientY, actions: desktopMenuActions() });
      }}
    >
      <div className="desktop-brand"><img src="/icons/nextos-192.png" alt="" /><span>NextOS</span></div>
      <div className="desktop-shortcuts" aria-label="Desktop-Verknüpfungen">
        {(['files', 'browser', 'office', 'mail', 'calendar'] as AppId[]).filter(appId => availableApps.has(appId)).map(appId => {
          const app = appCatalog[appId];
          return <button key={appId} onClick={() => launchApp(appId)}><span><app.icon style={{ color: app.accent }} /></span><small>{app.name}</small></button>;
        })}
      </div>
      <div className={`sync-indicator ${syncState}`}><span />{syncState === 'saved' ? 'Gespeichert' : syncState === 'saving' ? 'Speichert' : syncState === 'conflict' ? 'Konflikt' : 'Offline'}</div>
      <section className="window-layer" aria-label="Geöffnete Anwendungen">
        {workspace.windows.map(window => {
          const definition = appCatalog[window.appId];
          const Component = definition.component;
          return (
            <WindowFrame
              key={window.id}
              state={window}
              definition={definition}
              active={workspace.activeWindowId === window.id}
              desktopHidden={workspace.desktopId !== window.desktopId}
              onFocus={() => setWorkspace(state => focusWindow(state, window.id))}
              onClose={() => setWorkspace(state => closeWindow(state, window.id))}
              onMinimize={() => setWorkspace(state => minimizeWindow(state, window.id))}
              onMaximize={() => setWorkspace(state => maximizeWindow(state, window.id))}
              onSnap={bounds => snapWithAssist(window.id, bounds)}
              onBounds={bounds => setWorkspace(state => moveWindow(state, window.id, bounds))}
              onContextMenu={setContextMenu}
            >
              <Suspense fallback={<div className="app-loading"><span className="boot-spinner" /></div>}>
                <Component windowId={window.id} active={workspace.activeWindowId === window.id && !window.minimized} user={session.user} launchApp={launchApp} launchManagedApp={launchManagedApp} closeWindow={id => setWorkspace(state => closeWindow(state, id))} settings={settings} updateSettings={updateSettings} reportStatus={reportStatus} />
              </Suspense>
            </WindowFrame>
          );
        })}
      </section>
      {snapAssist && <section className="snap-assist" aria-label="Snap-Assistent"><header><strong>Fenster für die andere Seite auswählen</strong><button aria-label="Snap-Assistent schließen" onClick={() => setSnapAssist(null)}>×</button></header><div>{workspace.windows.filter(candidate => candidate.id !== snapAssist.windowId && candidate.desktopId === workspace.desktopId && !candidate.minimized).map(candidate => { const definition = appCatalog[candidate.appId]; return <button key={candidate.id} onClick={() => completeSnapGroup(candidate.id)}><definition.icon style={{ color: definition.accent }} /><span><strong>{candidate.title}</strong><small>{definition.description}</small></span></button>; })}</div></section>}
      <ShellChrome
        windows={workspace.windows}
        activeWindowId={workspace.activeWindowId}
        startOpen={startOpen}
        quickOpen={quickOpen}
        noticesOpen={noticesOpen}
        taskViewOpen={taskViewOpen}
        notices={notices}
        online={online}
        user={session.user}
        settings={settings}
        desktops={workspace.desktops}
        activeDesktopId={workspace.desktopId}
        snapGroups={workspace.snapGroups || []}
        availableApps={availableApps}
        managedApps={managedApps}
        canInstall={Boolean(installPrompt)}
        onInstall={() => void install()}
        onLaunch={launchApp}
        onLaunchManaged={appId => void launchManagedApp(appId)}
        onWindowPress={(window: WindowState) => setWorkspace(state => state.activeWindowId === window.id && !window.minimized ? minimizeWindow(state, window.id) : focusWindow(state, window.id))}
        onCloseWindow={windowId => setWorkspace(state => closeWindow(state, windowId))}
        onActivateSnapGroup={groupId => setWorkspace(state => activateSnapGroup(state, groupId))}
        onMoveWindow={(windowId, desktopId) => setWorkspace(state => moveWindowToDesktop(state, windowId, desktopId))}
        onCreateDesktop={() => { setWorkspace(state => createDesktop(state)); showDesktopWindows.current.clear(); }}
        onSwitchDesktop={desktopId => { setWorkspace(state => switchDesktop(state, desktopId)); setTaskViewOpen(false); showDesktopWindows.current.clear(); }}
        onRenameDesktop={(desktopId, name) => setWorkspace(state => renameDesktop(state, desktopId, name))}
        onRemoveDesktop={desktopId => setWorkspace(state => removeDesktop(state, desktopId))}
        onToggleStart={() => { setStartOpen(value => !value); setQuickOpen(false); setNoticesOpen(false); setTaskViewOpen(false); }}
        onToggleTaskView={() => { setTaskViewOpen(value => !value); setStartOpen(false); setQuickOpen(false); setNoticesOpen(false); }}
        onToggleQuick={() => { setQuickOpen(value => !value); setStartOpen(false); setNoticesOpen(false); setTaskViewOpen(false); }}
        onToggleNotices={() => {
          setNoticesOpen(value => {
            const opening = !value;
            if (opening) {
              const unread = notices.filter(item => !item.readAt).map(item => item.id);
              if (unread.length) {
                const readAt = new Date().toISOString();
                setNotices(items => items.map(item => unread.includes(item.id) ? { ...item, readAt } : item));
                void api.markNotificationsRead(unread).catch(() => {});
              }
            }
            return opening;
          });
          setStartOpen(false); setQuickOpen(false); setTaskViewOpen(false);
        }}
        onDismissNotice={id => { setNotices(items => items.filter(item => item.id !== id)); void api.deleteNotification(id).catch(() => {}); }}
        onClearNotices={() => { setNotices([]); void api.clearNotifications().catch(() => {}); }}
        onClosePanels={closePanels}
        onLogout={() => void logout()}
        onUpdateSettings={updateSettings}
      />
      {contextMenu && <ContextMenu menu={contextMenu} onClose={() => setContextMenu(null)} />}
      {needRefresh && <div className="update-toast"><RefreshCw /><span>NextOS-Update bereit</span><button onClick={() => void updateServiceWorker(true)}>Jetzt aktualisieren</button><button aria-label="Später" onClick={() => setNeedRefresh(false)}>×</button></div>}
    </main>
  );
}
